import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Zap, RefreshCw, Clock, AlertTriangle, DoorOpen, ShieldCheck } from "lucide-react";
import { Card } from "../common/Primitives";
import api, { formatApiError } from "../../lib/api";

/**
 * "Today at VenturaZ" digest card — mirror of what the Ask VenturaZ admin
 * chat live-context injection uses, so the card and chat never disagree.
 *
 * Polls once on mount + refresh button. Small enough to sit above the
 * main stat grid without pushing content off the fold.
 */
export default function DigestSnapshotCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/ai-chat/admin/digest-snapshot");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const generated = data?.generated_at ? new Date(data.generated_at).toLocaleTimeString() : "—";
  const rows = [
    { icon: Clock, label: "Under Review", value: data?.under_review ?? "—", accent: "text-ink", href: "/admin/kyc-queue" },
    { icon: AlertTriangle, label: "SLA breaches now", value: data?.breaches_now ?? "—",
      accent: (data?.breaches_now || 0) > 0 ? "text-rose-700" : "text-ink", href: "/admin/kyc-queue?filter=under_review" },
    { icon: AlertTriangle, label: "SLA breaches (last 7d)", value: data?.breaches_last_week ?? "—", accent: "text-ink" },
    { icon: DoorOpen, label: "Admin-opened rooms (7d)", value: data?.recent_admin_dr ?? "—", accent: "text-ink", href: "/admin/deal-rooms" },
    { icon: ShieldCheck, label: "Temp-approvals (30d)", value: data?.temp_approvals_30d ?? "—",
      accent: (data?.temp_approvals_30d || 0) > 0 ? "text-amber-700" : "text-ink" },
  ];

  return (
    <Card testId="admin-digest-snapshot" className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline flex items-center gap-1.5"><Zap size={12} className="text-gold" /> Today at VenturaZ</div>
          <div className="text-xs text-ink-soft mt-0.5">
            Same live counts the Ask VenturaZ chat uses · updated at {generated}
          </div>
        </div>
        <button
          onClick={load}
          disabled={busy}
          className="text-[11px] font-heading text-ink-muted hover:text-ink inline-flex items-center gap-1 disabled:opacity-40"
          data-testid="digest-snapshot-refresh"
          title="Refresh"
        >
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {rows.map((r) => {
          const inner = (
            <div className={`p-3 rounded-lg border border-navy/[0.08] bg-white ${r.href ? "hover:border-gold hover:shadow-sm transition" : ""}`}
                    data-testid={`digest-tile-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted flex items-center gap-1">
                <r.icon size={10} /> {r.label}
              </div>
              <div className={`font-heading text-2xl mt-0.5 ${r.accent}`}>{r.value}</div>
            </div>
          );
          return r.href ? (
            <Link key={r.label} to={r.href}>{inner}</Link>
          ) : (
            <div key={r.label}>{inner}</div>
          );
        })}
      </div>
      <div className="mt-3 text-[11px] text-ink-muted font-mono">
        SLA target: {data?.sla_hours ?? "—"}h · Approved (all-time): {data?.approved ?? "—"} · Rejected: {data?.rejected ?? "—"} · More Info: {data?.more_info ?? "—"}
      </div>
    </Card>
  );
}
